import * as z from "zod";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import { resetPassword, ResetPasswordInput } from "@aws-amplify/auth";

import { useFormWithSchema } from "hooks/useFormWithSchema";
import AuthLayout from "component/Layout/AuthLayout";
import { loginIdSchema } from "schemas/userSchema";
import { APP_ROUTES } from "routes/appRoutes";
import { useAppNavigate } from "routes/useAppNavigate";
import { notify } from "lib/notify";
import PlaceholderLogo from "./PlaceholderLogo";
import { handleError } from "utils/error/handleError";

const schema = z.object({
  loginId: loginIdSchema,
});

type ResetFormInput = z.infer<typeof schema>;

const PasswordResetRequestPage = () => {
  const { publicNavigate } = useAppNavigate();
  const onSubmit = async (data: ResetFormInput) => {
    try {
      const params: ResetPasswordInput = {
        username: data.loginId,
      };

      await resetPassword(params);
      notify("検証コードを送信しました。");
      publicNavigate.passwordResetVerify({ state: { loginId: data.loginId } });
    } catch (err) {
      handleError(err, {
        overrideMessage: "エラーが発生しています。再度お試しください。",
      });
    }
  };

  const {
    formState: { isSubmitting, errors },
    register,
    onSubmitHandler,
  } = useFormWithSchema(schema, onSubmit);

  return (
    <AuthLayout>
      <Box
        component={"form"}
        onSubmit={onSubmitHandler}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Stack
          spacing={4}
          sx={{
            width: "100%",
          }}
        >
          <Stack spacing={2}>
            <Box display={"flex"} justifyContent={"center"}>
              <PlaceholderLogo />
            </Box>
            <Typography variant="h5">パスワードを忘れたら</Typography>
            <Typography variant="body2" color="text.secondary">
              登録されているメールアドレス宛に検証コードを送信します。
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <TextField
              label="ログインID"
              {...register("loginId")}
              error={!!errors.loginId}
              helperText={errors.loginId?.message}
              fullWidth
              name="loginId"
              type="text"
              disabled={isSubmitting}
              autoComplete="login-id"
            />
          </Stack>
          <Stack spacing={2}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              startIcon={
                isSubmitting && <CircularProgress size={20} color="inherit" />
              }
            >
              送信
            </Button>
            <Box textAlign={"center"}>
              <MuiLink
                sx={{
                  display: "inline-block",
                  width: "fit-content",
                }}
                component={RouterLink}
                to={APP_ROUTES.public.login.path}
              >
                <Typography variant="body2">ログイン画面に戻る</Typography>
              </MuiLink>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default PasswordResetRequestPage;
